/* eslint-disable react-hooks/exhaustive-deps */
import {
  AddOutlined,
  ArrowBack,
  DirectionsBusOutlined, InfoOutlined, RemoveOutlined,
  WarningAmber
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  addHours,
  addMinutes,
  differenceInHours,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  subHours,
  subMinutes,
} from "date-fns";
import i18next from "i18next";
import { useConfirm } from "material-ui-confirm";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import MessageBox from "./Components/message-box";
import { colors } from "./config/colors";
import {
  DATE,
  DURATION,
  LEAVE_REQUEST_CODE,
  LEAVE_TYPE,
  SHIFT_ARRIVE_LATE_CODE,
  SHIFT_CANCEL_CODE,
  SHIFT_LEAVE_EARLY_CODE,
  SHIFT_SWAP_CODE,
  START_TIME,
} from "./config/constants";
import useWebWorkerData from "./controllers/use-web-worker-data";
import {
  closeLeaveRequestPage,
  closeViewRegularShiftPage,
  presetSubmitLeaveRequest,
  resetSubmitLeaveRequest, setWidthdrawLeaveRequestFtShiftId,
  submitLeaveRequest, withdrawLeaveRequest
} from "./store/web-worker-slice";
import {
  getDateDifferenceString,
  getHourMinuteDifference,
  getHoursAndMinutesStringFromFloat,
  getTranslatedDate
} from "./util/helper";

const useStyles = makeStyles(() => ({
  pageContainer: {
    backgroundColor: colors.light,
    height: "100dvh",
    display: "flex",
    flexDirection: "column",
  },
  shiftCard: {
    marginBottom: 24,
    marginTop: 16,
    borderRadius: "8px",
    backgroundColor: "#F2F6FF",
  },

  title: {
    flexGrow: 1,
  },
}));

const getLocalizedDurationString = (minutes: number) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  const hourStr =
    hrs > 0
      ? `${hrs} ${
          hrs === 1 ? i18next.t("helpers_hour-text") : i18next.t("helpers_hours-text")
        }`
      : "";
  const minStr =
    mins > 0
      ? `${mins} ${
          mins === 1
            ? i18next.t("helpers_minute-text")
            : i18next.t("helpers_minutes-text")
        }`
      : "";

  return [hourStr, minStr].filter(Boolean).join(" ").trim();
};

// const generateDurationOptions = (
//   startDate: number | Date,
//   endDate: number | Date,
//   incrementHours: string
// ) => {
//   const returnValue = [];
//   try {
//     const maxDurationInMinutes = differenceInMinutes(endDate, startDate);
//     let increment = parseFloat(incrementHours);
//
//     if (isNaN(increment) || increment <= 0) increment = 1;
//     const incrementInMinutes = Math.round(increment * 60);
//
//     let currentDuration = incrementInMinutes;
//     while (currentDuration < maxDurationInMinutes) {
//       returnValue.push({
//         text: getLocalizedDurationString(currentDuration),
//         timeInMinutes: currentDuration,
//       });
//       currentDuration += incrementInMinutes;
//     }
//
//     return returnValue;
//   } catch (e) {
//     return returnValue;
//   }
// };

export const generateDurationOptionsByMinutes = (
  maxDurationInMinutes: number,
  incrementHours: string
) => {
  const returnValue = [];
  try {
    let increment = parseFloat(incrementHours);
    if (isNaN(increment) || increment <= 0) increment = 1;

    const incrementInMinutes = Math.round(increment * 60);
    if (incrementInMinutes === 0) return [];

    for (let currentDuration = incrementInMinutes; currentDuration <= maxDurationInMinutes; currentDuration += incrementInMinutes) {
      returnValue.push({
        text: getLocalizedDurationString(currentDuration),
        timeInMinutes: currentDuration,
      });
    }
    return returnValue;
  } catch (e) {
    return returnValue;
  }
};

const getIndexesBasedOnShiftDateTime = (
  availableHours: any,
  availableMinutes: any,
  selectedShiftDateTime: any
) => {
  const returnValue: any = {
    hoursIndex: 0,
    minutesIndex: 0,
    amPmIndex: 0,
  };
  try {
    const shiftStartHour = selectedShiftDateTime.getHours() % 12 || 12;
    const shiftStartAmPmIndex = selectedShiftDateTime.getHours() > 11 ? 1 : 0;
    const shiftStartMinutes = selectedShiftDateTime.getMinutes();
    for (
      let startTimeHourIndex = 0;
      startTimeHourIndex < availableHours.length;
      startTimeHourIndex++
    ) {
      let selectedHour = parseInt(availableHours[startTimeHourIndex]);
      if (startTimeHourIndex === 0) {
        selectedHour = 0;
      }
      if (selectedHour >= shiftStartHour) {
        if (parseInt(availableHours[startTimeHourIndex]) === shiftStartHour) {
          // Check min only if shift start time and first allowed start time is same
          for (
            let startTimeMinuteIndex = 0;
            startTimeMinuteIndex < availableMinutes.length;
            startTimeMinuteIndex++
          ) {
            if (
              parseInt(availableMinutes[startTimeMinuteIndex]) >=
              shiftStartMinutes
            ) {
              returnValue.minutesIndex = startTimeMinuteIndex;
              break;
            }
          }
        }
        returnValue.hoursIndex = startTimeHourIndex;
        returnValue.amPmIndex = shiftStartAmPmIndex;
        break;
      }
    }
    return returnValue;
  } catch (e) {
    return returnValue;
  }
};

const sanitizeString = (str: string) => str.replace(/[{}]/g, "");
const renderLeaveText = (item: any) => {
  try {
    const sanitizedItemName = sanitizeString(item.name || "");
    const showBalance = !item.isNoLimit && item.availableHrs !== null;
    const availableHrsAndMin = showBalance ? getHoursAndMinutesStringFromFloat(item.availableHrs ?? 0) : null;

    return  <Typography
      sx={{
        color: colors.fieldText,
        whiteSpace: "wrap",
      }}
    >{sanitizedItemName}{showBalance && ` (${availableHrsAndMin})`}</Typography>;
  } catch (e) {
    return "[Error]";
  }
};

const renderSecondLeaveText = (item: any, firstLeaveDuration = 0, shiftDuration = 0, type) => {
  try {
    let returnValue = "";

    const sanitizedItemName = sanitizeString(item.name || "");
    const showBalance = !item.isNoLimit && item.availableHrs !== null;
    const availableHrsAndMin = showBalance ? getHoursAndMinutesStringFromFloat(item.availableHrs ?? 0) : null;

    returnValue = showBalance ? `${sanitizedItemName} (${availableHrsAndMin})` : sanitizedItemName;

    const minIncrement = Math.round(item.incrementHours * 60);
    let inactiveText = "";
    if (shiftDuration && shiftDuration - firstLeaveDuration < minIncrement && type !== SHIFT_CANCEL_CODE) {
      // This is inactive leave type option
      inactiveText = `${i18next.t("apply-for-leave_second-time-off-type_not_valid", {minDuration: getLocalizedDurationString(minIncrement)})}`;
    }

    return <MenuItem key={`second-time-off-${item.id}`} disabled={inactiveText !== ""} value={item}><Typography
      sx={{
        color: colors.fieldText,
        whiteSpace: "wrap",
      }}
    >{returnValue} {inactiveText && <Typography
      component="span"
      sx={{
        fontStyle: "italic",
      }}>{inactiveText}</Typography>}</Typography></MenuItem>;
  } catch (e) {
    return "[Error]";
  }
};

const defaultHours = [
  "12",
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
];

const defaultMinutes = ["00", "15", "30", "45"];
const defaultAmPm = ["AM", "PM"];

const LeaveRequestPage: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const { i18n, t } = useTranslation();
  const scrollViewRef = useRef<HTMLDivElement | null>(null);
  const dropdownRefForleaveRequestType = useRef(null);
  const dropdownRefForleaveRequestType2 = useRef(null);
  const dropdownRefForDurationType = React.useRef<HTMLInputElement>(null);
  const dropdownRefForDurationType2 = React.useRef<HTMLInputElement>(null);

  const [text, setText] = useState("");
  const [editingMessage, setEditingMessage] = useState(false);

  const {
    leaveRequestPageType: type,
    leaveRequestPageShift: shift,
    leaveTypes,
    requestTypes,
    timeOffEnabled,
    submitLeaveRequestErrorMessage,
    submitLeaveRequestStatus,
    availableLeaveTypes,
    companyId,
    pairingEnabled,
  } = useWebWorkerData();

  const [canSubmit, setCanSubmit] = useState(false);
  const [focusedField, setFocusedField] = useState("");
  const [duration, setDuration] = useState<null | number>(null);
  const [timeDifferanceString, setTimeDifferenceString] = useState("0 hours");
  const [workerComment, setWorkerComment] = useState("");
  const [requestTypeId, setRequestTypeId] = useState(undefined);

  const [durationOptions, setDurationOptions] = useState<any>([]);

  const [submittingLeaveRequest, setSubmittingLeaveRequest] = useState(false);

  const [selectedLeaveType2, setSelectedLeaveType2] = useState<any>();
  const [duration2, setDuration2] = useState<null | number>(null);

  const [isPairingAccordionOpen, setIsPairingAccordionOpen] = useState(false);

  const [durationOptions2, setDurationOptions2] = useState<any[]>([]);
  const [selectedDurationIndex2, setSelectedDurationIndex2] = useState(null);
  const [isDurationValid, setIsDurationValid] = useState(true);

  const [timePickerPosition, setTimePickerPosition] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const [openCalendar, setOpenCalendar] = useState(false);
  const [openStartTimePicker, setOpenStartTimePicker] = useState(false);
  const [selectedHourIndex, setSelectedHourIndex] = useState(0);
  const [selectedMinutesIndex, setSelectedMinutesIndex] = useState(0);
  const [selectedAmpmIndex, setSelectedAmpmIndex] = useState(0);
  const [hours, setHours] = useState(defaultHours);
  const [minutes, setMinutes] = useState(defaultMinutes);
  const [ampm, setAmpm] = useState(defaultAmPm);
  const [finalStartTime, setFinalStartTime] = useState<any>(undefined);

  const [selectedLeaveType, setSelectedLeaveType] = useState<any>();


  useEffect(() => {
    try {
      if (
        shift &&
        finalStartTime !== undefined &&
        selectedLeaveType
      ) {
        const shiftStartTimeDate = new Date(
          shift.startDateTime
        );
        let shiftEndTimeDate = new Date(shift.endDateTime);
        if (type !== SHIFT_CANCEL_CODE) {
          shiftEndTimeDate = new Date(
            subHours(
              new Date(shift.endDateTime),
              Number(selectedLeaveType.incrementHours)
            )
          );
        }
        const finalAmpm = finalStartTime.ampm;
        const finalMinutes = parseInt(finalStartTime.minutes);
        let finalHours = parseInt(finalStartTime.hours);
        if (finalHours === 12) {
          finalHours = 0;
        }
        if (finalAmpm.toUpperCase() === "PM") {
          finalHours += 12;
        }
        let startTimeDate = new Date(
          shiftStartTimeDate.getFullYear(),
          shiftStartTimeDate.getMonth(),
          shiftStartTimeDate.getDate(),
          0,
          0,
          0,
          0
        );
        let endTimeDate = new Date(
          shiftEndTimeDate.getFullYear(),
          shiftEndTimeDate.getMonth(),
          shiftEndTimeDate.getDate(),
          0,
          0,
          0,
          0
        );
        startTimeDate = new Date(addHours(startTimeDate, finalHours));
        startTimeDate = new Date(addMinutes(startTimeDate, finalMinutes));
        endTimeDate = new Date(addHours(endTimeDate, finalHours));
        endTimeDate = new Date(addMinutes(endTimeDate, finalMinutes));

        if (startTimeDate.getDate() !== endTimeDate.getDate()) {
          if (
            endTimeDate.getTime() >= shiftEndTimeDate.getTime() &&
            startTimeDate.getTime() <= shiftStartTimeDate.getTime()
          ) {
            if (
              differenceInMinutes(endTimeDate, shiftEndTimeDate) >
              differenceInMinutes(shiftStartTimeDate, startTimeDate)
            ) {
              // When start time is closer to shift start time
              const indexValuesBasedOnStartDateTime =
                getIndexesBasedOnShiftDateTime(
                  hours,
                  minutes,
                  shiftStartTimeDate
                );
              setSelectedHourIndex(indexValuesBasedOnStartDateTime.hoursIndex);
              setSelectedMinutesIndex(
                indexValuesBasedOnStartDateTime.minutesIndex
              );
              setSelectedAmpmIndex(indexValuesBasedOnStartDateTime.amPmIndex);
            } else {
              // When start time is closer to shift end time
              const indexValuesBasedOnEndDateTime =
                getIndexesBasedOnShiftDateTime(
                  hours,
                  minutes,
                  shiftEndTimeDate
                );
              setSelectedHourIndex(indexValuesBasedOnEndDateTime.hoursIndex);
              setSelectedMinutesIndex(
                indexValuesBasedOnEndDateTime.minutesIndex
              );
              setSelectedAmpmIndex(indexValuesBasedOnEndDateTime.amPmIndex);
            }
          }
        } else {
          if (startTimeDate.getTime() <= shiftStartTimeDate.getTime()) {
            // When start time is closer to shift start time
            const indexValuesBasedOnStartDateTime =
              getIndexesBasedOnShiftDateTime(
                hours,
                minutes,
                shiftStartTimeDate
              );
            setSelectedHourIndex(indexValuesBasedOnStartDateTime.hoursIndex);
            setSelectedMinutesIndex(
              indexValuesBasedOnStartDateTime.minutesIndex
            );
            setSelectedAmpmIndex(indexValuesBasedOnStartDateTime.amPmIndex);
          } else if (startTimeDate.getTime() >= shiftEndTimeDate.getTime()) {
            // When start time is closer to shift end time
            const indexValuesBasedOnEndDateTime =
              getIndexesBasedOnShiftDateTime(hours, minutes, shiftEndTimeDate);
            setSelectedHourIndex(indexValuesBasedOnEndDateTime.hoursIndex);
            setSelectedMinutesIndex(indexValuesBasedOnEndDateTime.minutesIndex);
            setSelectedAmpmIndex(indexValuesBasedOnEndDateTime.amPmIndex);
          }
        }
      }
    } catch (e) {
      // Do nothing
    }
  }, [
    finalStartTime,
    shift,
    selectedLeaveType,
    type,
  ]);

  // useEffect(() => {
  //   try {
  //     if (
  //       newShiftStartTime !== undefined &&
  //       shift &&
  //       selectedLeaveType
  //     ) {
  //       if (type === SHIFT_CANCEL_CODE) {
  //         setDuration(
  //           getHourMinuteDifference(
  //             new Date(shift.endDateTime),
  //             new Date(shift.startDateTime)
  //           )?.differenceInMinutes
  //         );
  //       } else {
  //         setDuration(0);
  //         setDurationOptions(
  //           generateDurationOptions(
  //             newShiftStartTime,
  //             new Date(shift.endDateTime),
  //             selectedLeaveType.incrementHours
  //           )
  //         );
  //       }
  //     }
  //   } catch (e) {
  //     // Do nothing
  //   }
  // }, [
  //   newShiftStartTime,
  //   shift,
  //   selectedLeaveType,
  //   type,
  // ]);

  useEffect(() => {
    if (shift) {
      setTimeDifferenceString(
        getHourMinuteDifference(
          new Date(shift.endDateTime),
          new Date(shift.startDateTime)
        )?.differenceInText
      );
    }
  }, [shift]);

  useEffect(() => {
    try {
      if (
        hours.length > 0 &&
        minutes.length > 0 &&
        shift &&
        selectedLeaveType
      ) {
        if (
          Object.prototype.hasOwnProperty.call(
            shift,
            "startDateTime"
          )
        ) {
          const indexValues = getIndexesBasedOnShiftDateTime(
            hours,
            minutes,
            new Date(shift.startDateTime)
          );
          setSelectedMinutesIndex(indexValues.minutesIndex);
          setSelectedHourIndex(indexValues.hoursIndex);
          setSelectedAmpmIndex(indexValues.amPmIndex);
        }
      }
    } catch (e) {
      // Do nothing
    }
  }, [shift, hours, minutes, selectedLeaveType]);

  useEffect(() => {
    if (submitLeaveRequestStatus === "pending") {
      setSubmittingLeaveRequest(true);
    } else {
      setSubmittingLeaveRequest(false);
      if (submitLeaveRequestStatus === "fulfilled") {
        dispatch(resetSubmitLeaveRequest());
        dispatch(closeLeaveRequestPage());
        dispatch(closeViewRegularShiftPage());
      }
    }
  }, [submitLeaveRequestStatus]);

  useEffect(() => {
    try {
      if (requestTypes.length !== 0) {
        const leaveRequest = requestTypes.find(
          (rType: any) => rType.code.toLowerCase() === type
        );

        if (leaveRequest) {
          setRequestTypeId(leaveRequest.id);
        }
      }
    } catch (e) {
      // Do nothing
    }
  }, [requestTypes, type]);

  useEffect(() => {
    if (openCalendar) {
      setFocusedField(DATE);
    } else {
      setFocusedField("");
    }
  }, [openCalendar]);

  useEffect(() => {
    if (openStartTimePicker) {
      setFocusedField(START_TIME);
    } else {
      setFocusedField("");
    }
  }, [openStartTimePicker]);

  // useEffect(() => {
  //   try {
  //     if (timeOffEnabled && selectedLeaveType) {
  //       setDurationOptions(
  //         generateDurationOptions(
  //           new Date(shift.startDateTime),
  //           new Date(shift.endDateTime),
  //           selectedLeaveType.incrementHours
  //         )
  //       );
  //     }
  //   } catch (e) {
  //     // Do nothing
  //   }
  // }, [selectedLeaveType, selectedDate, timeOffEnabled, shift]);

  const shiftDurationInMinutes = useMemo(() => {
    if (!shift) return 0;
    return getHourMinuteDifference(new Date(shift.endDateTime), new Date(shift.startDateTime)).differenceInMinutes;
  }, [shift]);

  // MODIFIED: Logic for when PRIMARY leave type is selected
  useEffect(() => {
    if (selectedLeaveType && shift) {
      // Reset secondary selection when primary changes
      setSelectedLeaveType2(null);
      setDuration2(null);

      if (type === SHIFT_CANCEL_CODE) {
        // Case A: Full shift. Max duration IS the full shift.
        const options = generateDurationOptionsByMinutes(shiftDurationInMinutes, selectedLeaveType.incrementHours);
        const lastOption = options?.[options.length - 1]?.timeInMinutes;
        if (lastOption !== shiftDurationInMinutes) {
          const completeOptions = [...options, {
            text: getLocalizedDurationString(shiftDurationInMinutes),
            timeInMinutes: shiftDurationInMinutes,
          }];
          setDurationOptions(completeOptions);
          setDuration(completeOptions?.[completeOptions.length - 1]?.timeInMinutes ?? null);
        } else {
          setDurationOptions(options);
          setDuration(options?.[options.length - 1]?.timeInMinutes ?? null);
        }

      } else {
        // Case B: Partial shift. Max duration MUST BE LESS than the full shift.
        const smallestIncrement = Math.round(selectedLeaveType.incrementHours * 60);
        const maxSelectableDuration = shiftDurationInMinutes - smallestIncrement;

        if (maxSelectableDuration) {
          const options = generateDurationOptionsByMinutes(
            maxSelectableDuration,
            selectedLeaveType.incrementHours
          );
          setDurationOptions(options);
          setDuration(prevState => {
            if (prevState && prevState > options?.[options.length - 1]?.timeInMinutes) {
              return options?.[options.length - 1]?.timeInMinutes ?? null;
            }
            return prevState;
          });
        } else {
          setDurationOptions([]); // No valid options if max is less than smallest increment
          setDuration(null);
        }
      }
    }
  }, [selectedLeaveType, shift, type, shiftDurationInMinutes]);

// NEW Logic for when SECONDARY leave type is selected
  useEffect(() => {
    if (selectedLeaveType2 && shift) {
      const minSecondaryIncrement = Math.round(selectedLeaveType2.incrementHours * 60);
      let maxPrimaryDuration;

      if (type === SHIFT_CANCEL_CODE) {
        // For a Full Shift, the max is total shift minus the minimum for the second type.
        maxPrimaryDuration = shiftDurationInMinutes - minSecondaryIncrement;
        const options = generateDurationOptionsByMinutes(
          maxPrimaryDuration >= 0 ? maxPrimaryDuration : 0,
          selectedLeaveType.incrementHours
        );
        const lastOption = options?.[options.length - 1]?.timeInMinutes;
        if (lastOption !== maxPrimaryDuration) {
          const completeOptions = [...options, {
            text: getLocalizedDurationString(maxPrimaryDuration),
            timeInMinutes: maxPrimaryDuration,
          }];
          setDurationOptions(completeOptions);
          setDuration(completeOptions?.[completeOptions.length - 1]?.timeInMinutes ?? null);
        } else {
          setDurationOptions(options);
          setDuration(options?.[options.length - 1]?.timeInMinutes ?? null);
        }
      } else {
        // For Partial Day, the max is total shift minus minimum for the second type, minus 1.
        // This ensures the total can never equal the full shift duration.
        maxPrimaryDuration = shiftDurationInMinutes - minSecondaryIncrement - 1;
        if (maxPrimaryDuration < 0) {
          maxPrimaryDuration = 0; // Ensure we don't have negative durations
        }
        const options = generateDurationOptionsByMinutes(
          maxPrimaryDuration,
          selectedLeaveType.incrementHours
        );
        setDurationOptions(options);
        setDuration(prevState => {
          if (prevState && prevState > options?.[options.length - 1]?.timeInMinutes) {
            return options?.[options.length - 1]?.timeInMinutes ?? null;
          }
          return prevState;
        });
      }
    } else if (!selectedLeaveType2 && selectedLeaveType && shift) {
      // Logic to restore primary options when secondary is deselected/reset
      if (type === SHIFT_CANCEL_CODE) {
        // SPECIAL CASE: For full shift callouts, when pairing is removed,
        // reset the primary duration to the full shift duration.
        const options = generateDurationOptionsByMinutes(shiftDurationInMinutes, selectedLeaveType.incrementHours);
        const lastOption = options?.[options.length - 1]?.timeInMinutes;
        if (lastOption !== shiftDurationInMinutes) {
          const completeOptions = [...options, {
            text: getLocalizedDurationString(shiftDurationInMinutes),
            timeInMinutes: shiftDurationInMinutes,
          }];
          setDurationOptions(completeOptions);
          setDuration(completeOptions?.[completeOptions.length - 1]?.timeInMinutes ?? null);
        } else {
          setDurationOptions(options);
          setDuration(options?.[options.length - 1]?.timeInMinutes ?? null);
        }
      } else {
        // For partial day requests, just restore the full list of duration options
        const smallestIncrement = Math.round(selectedLeaveType.incrementHours * 60);
        const maxSelectableDuration = shiftDurationInMinutes - smallestIncrement;
        const fullOptions = generateDurationOptionsByMinutes(
          maxSelectableDuration > 0 ? maxSelectableDuration : 0,
          selectedLeaveType.incrementHours
        );
        setDurationOptions(fullOptions);
        setDuration(prevState => {
          if (prevState && prevState > fullOptions?.[fullOptions.length - 1]?.timeInMinutes) {
            return fullOptions?.[fullOptions.length - 1]?.timeInMinutes ?? null;
          }
          return prevState;
        });
      }
    }
  }, [selectedLeaveType2, shift]);

  useEffect(() => {
    if (duration && selectedLeaveType2 && shift) {
      if (type === SHIFT_CANCEL_CODE) {
        const options = generateDurationOptionsByMinutes(shiftDurationInMinutes - duration, selectedLeaveType2.incrementHours);
        const lastOption = options?.[options.length - 1]?.timeInMinutes;
        if (lastOption !== shiftDurationInMinutes - duration) {
          const completeOptions = [...options, {
            text: getLocalizedDurationString(shiftDurationInMinutes - duration),
            timeInMinutes: shiftDurationInMinutes - duration,
          }];
          setDurationOptions2(completeOptions);
          setDuration2(completeOptions?.[completeOptions.length - 1]?.timeInMinutes ?? null);
        } else {
          setDurationOptions2(options);
          setDuration2(options?.[options.length - 1]?.timeInMinutes ?? null);
        }
      } else {
        const remainingMinutes = shiftDurationInMinutes - duration;
        const maxSecondaryDuration = remainingMinutes - 1;
        const options = generateDurationOptionsByMinutes(
          maxSecondaryDuration >= 0 ? maxSecondaryDuration : 0,
          selectedLeaveType2.incrementHours
        );
        setDurationOptions2(options);
        setDuration2(options?.[0]?.timeInMinutes ?? null);
      }
    }
  }, [duration, selectedLeaveType2, shift]);

  // NEW: Logic for when PRIMARY duration changes, update secondary options
  useEffect(() => {
    if (duration) {
      if (selectedLeaveType2 && type !== SHIFT_CANCEL_CODE) {
        // Case B: Recalculate secondary options when primary duration changes
         const remainingMinutes = shiftDurationInMinutes - duration;

        // For partial day requests, the max secondary duration must be less than the remaining time
        const maxSecondaryDuration = remainingMinutes - 1;
        const options = generateDurationOptionsByMinutes(
          maxSecondaryDuration >= 0 ? maxSecondaryDuration : 0,
          selectedLeaveType2.incrementHours
        );
        setDurationOptions2(options);
        setDuration2(options?.[0]?.timeInMinutes ?? null);
      } else if (selectedLeaveType2 && type === SHIFT_CANCEL_CODE) {
        const options = generateDurationOptionsByMinutes(
          shiftDurationInMinutes - duration,
          selectedLeaveType2.incrementHours);
        const lastOption = options?.[options.length - 1]?.timeInMinutes;
        if (lastOption !== shiftDurationInMinutes - duration) {
          const completeOptions = [...options, {
            text: getLocalizedDurationString(shiftDurationInMinutes - duration),
            timeInMinutes: shiftDurationInMinutes - duration,
          }];
          setDurationOptions2(completeOptions);
          setDuration2(completeOptions?.[completeOptions.length - 1]?.timeInMinutes ?? null);
        } else {
          setDurationOptions2(options);
          setDuration2(options?.[options.length - 1]?.timeInMinutes ?? null);
        }
      }
    }
  }, [duration]);

  // Determine if the pairing accordion should be shown.
  const showPairingAccordion = useMemo(() => {
    // Accordion should be shown only if a first leave type is selected, and it has pairing enabled.
    if (!selectedLeaveType || !selectedLeaveType.pairingEnabled) {
      return false;
    }
    // Also, there must be at least one other leave type with pairing enabled.
    const otherPairingEnabledTypes = availableLeaveTypes.filter(
      (type:any) => type.pairingEnabled && type.id !== selectedLeaveType.id
    );
    return otherPairingEnabledTypes.length > 0;
  }, [selectedLeaveType, availableLeaveTypes]);

  // MODIFIED Submission eligibility logic
  useEffect(() => {
    const firstDuration = duration ?? 0;
    const secondDuration = duration2 ?? 0;
    const totalDuration = firstDuration + secondDuration;

    // Define the maximum allowed duration based on the request type
    const maxAllowedDuration = (type === SHIFT_CANCEL_CODE)
      ? shiftDurationInMinutes
      : shiftDurationInMinutes - 1; // For partial requests, total must be less than the full shift

    if (
      requestTypeId &&
      selectedLeaveType &&
      totalDuration > 0 &&
      totalDuration <= maxAllowedDuration // Use the new stricter max duration
    ) {
      if (showPairingAccordion && selectedLeaveType2 && duration2) {
        setCanSubmit(duration2 > 0);
      } else {
        setCanSubmit(true);
      }
    } else {
      setCanSubmit(false);
    }
  }, [requestTypeId, selectedLeaveType, duration, duration2, shiftDurationInMinutes, showPairingAccordion, type]);

  // Create a filtered list for the second dropdown.
  const pairedLeaveTypes = useMemo(() => {
    if (!availableLeaveTypes || !selectedLeaveType) {
      return [];
    }
    // Filter the list to include only other pairing-enabled types
    return availableLeaveTypes.filter(
      (type:any) => type.pairingEnabled && type.id !== selectedLeaveType.id
    );
  }, [availableLeaveTypes, selectedLeaveType]);

  // useEffect(() => {
  //   try {
  //     if (
  //       requestTypeId !== undefined &&
  //       selectedLeaveType !== undefined &&
  //       duration !== undefined &&
  //       duration >= 15 &&
  //       newShiftStartTime instanceof Date
  //     ) {
  //       setCanSubmit(true);
  //     } else if (!timeOffEnabled) {
  //       setCanSubmit(true);
  //     } else {
  //       setCanSubmit(false);
  //     }
  //   } catch (e) {
  //     setCanSubmit(false);
  //   }
  // }, [
  //   newShiftStartTime,
  //   requestTypeId,
  //   selectedLeaveType,
  //   duration,
  //   timeOffEnabled,
  //   selectedDate,
  // ]);

  // useEffect calculates and formats the dynamic text for the info bar
  useEffect(() => {
    const firstDuration = duration ?? 0;
    const secondDuration = duration2 ?? 0;
    const totalDuration = firstDuration + secondDuration;

    if (!shift || totalDuration === 0) {
      setIsDurationValid(true); // Reset to valid when there's no duration
      return;
    }

    let newTime;
    let isValid = true;

    const shiftStartTime = new Date(shift.startDateTime);
    const shiftEndTime = new Date(shift.endDateTime);

    if (type === SHIFT_ARRIVE_LATE_CODE) {
      newTime = addMinutes(shiftStartTime, totalDuration);
      if (isAfter(newTime, shiftEndTime) || totalDuration >= shiftDurationInMinutes) {
        // Fails if time is after shift end OR duration is the entire shift
        isValid = false;
      }
    } else if (type === SHIFT_LEAVE_EARLY_CODE) {
      newTime = subMinutes(shiftEndTime, totalDuration);
      if (isBefore(newTime, shiftStartTime) || totalDuration >= shiftDurationInMinutes) {
        // Fails if time is before shift start OR duration is the entire shift
        isValid = false;
      }
    }

    setIsDurationValid(isValid);

  }, [duration, duration2, type, shift, shiftDurationInMinutes]);

  useEffect(() => {
    // Set final date start time
    setFinalStartTime({
      hours: hours[selectedHourIndex],
      minutes: minutes[selectedMinutesIndex],
      ampm: ampm[selectedAmpmIndex],
    });
  }, []);

  // const getStartDate = () => {
  //   try {
  //     let returnValue = new Date(shift.startDateTime);
  //     const endDateTime = new Date(shift.endDateTime);
  //     switch (type) {
  //       case SHIFT_LEAVE_EARLY_CODE:
  //         returnValue = new Date(subMinutes(endDateTime, duration));
  //         break;
  //       default:
  //         break;
  //     }
  //     return returnValue.toISOString();
  //   } catch (e) {
  //     // Do nothing
  //     return new Date().toISOString();
  //   }
  // };
  //
  // const getEndDate = () => {
  //   try {
  //     let returnValue = new Date(shift.endDateTime);
  //     const startDateTime: any = new Date(shift.startDateTime);
  //     switch (type) {
  //       case SHIFT_ARRIVE_LATE_CODE:
  //         returnValue = new Date(addMinutes(startDateTime, duration));
  //         break;
  //       default:
  //         break;
  //     }
  //     return returnValue.toISOString();
  //   } catch (e) {
  //     // Do nothing
  //     return new Date().toISOString();
  //   }
  // };

  // MODIFIED: Correctly calculates start/end dates based on total duration
  const getStartDate = () => {
    const firstDuration = duration ?? 0;
    const secondDuration = duration2 ?? 0;
    const totalDuration = firstDuration + secondDuration;
    if (type === SHIFT_LEAVE_EARLY_CODE) {
      // For leave early, the effective start time is the shift end time minus the duration
      return new Date(new Date(shift.endDateTime).getTime() - totalDuration * 60000).toISOString();
    }
    // For full shift or arrive late, the start time is the shift's start time
    return new Date(shift.startDateTime).toISOString();
  };

  const getEndDate = () => {
    const firstDuration = duration ?? 0;
    const secondDuration = duration2 ?? 0;
    const totalDuration = firstDuration + secondDuration;
    if (type === SHIFT_ARRIVE_LATE_CODE) {
      // For arrive late, the effective end time is the shift start time plus the duration
      return new Date(new Date(shift.startDateTime).getTime() + totalDuration * 60000).toISOString();
    }
    // For full shift or leave early, the end time is the shift's end time
    return new Date(shift.endDateTime).toISOString();
  };

  const getHeaderText = () => {
    let returnValue = i18n.t("apply-for-leave_apply-for-time-off");
    switch (type) {
      case SHIFT_SWAP_CODE:
        returnValue = i18n.t("apply-for-leave_request-shift-swap");
        break;
      case SHIFT_CANCEL_CODE:
        returnValue = i18n.t("apply-for-leave_request-to-time-off");
        break;
      case SHIFT_ARRIVE_LATE_CODE:
        returnValue = i18n.t("apply-for-leave_request-arrive_late");
        break;
      case SHIFT_LEAVE_EARLY_CODE:
        returnValue = i18n.t("apply-for-leave_request-leave-early");
        break;
      default:
        break;
    }
    return returnValue;
  };

  const canShowEstimatedTime = useMemo(() => {
    // Show estimated time only for arrive late and leave early requests
    // Only show when at least one of the durations is set
    return (type === SHIFT_ARRIVE_LATE_CODE || type === SHIFT_LEAVE_EARLY_CODE) && ((duration ?? 0) > 0 || (duration2 ?? 0) > 0);
  }, [
    type,
    duration,
    duration2
  ]);

  const Header = () => (
    <Box display="flex" alignItems="center" p={2} bgcolor={colors.primary}>
      <IconButton
        sx={{
          width: "44px",
        }}
        onClick={() => {
          try {
            dispatch(closeLeaveRequestPage());
          } catch (e) {
            // Do nothing
          }
        }}
      >
        <ArrowBack
          sx={{
            color: colors.light,
          }}
        />
      </IconButton>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "row",
        }}
      >
        <Typography
          sx={{
            color: colors.light,
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {getHeaderText()}
        </Typography>
      </Box>
      <Box
        sx={{
          width: "44px",
        }}
      />
    </Box>
  );

  const shiftCard = () => {
    const leaveRequestTypeCode= type;
    const leaveRequestStartDate= getStartDate();
    const leaveRequestEndDate= getEndDate();


    // NEW: Logic to generate the info bar text
    let infoBarText = "";
    if (leaveRequestTypeCode === SHIFT_ARRIVE_LATE_CODE && leaveRequestEndDate) {
      const newTime = new Date(leaveRequestEndDate);
      infoBarText = i18next.t("apply-for-leave_expected-arrival-time", { time: format(newTime, "p") });
    } else if (leaveRequestTypeCode === SHIFT_LEAVE_EARLY_CODE && leaveRequestStartDate) {
      const newTime = new Date(leaveRequestStartDate);
      infoBarText = i18next.t("apply-for-leave_new-departure-time", { time: format(newTime, "p") });
    }
    return (
      <Paper
        elevation={3}
        key={shift?.shiftName}
        className={classes.shiftCard}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingLeft: "24px",
            paddingRight: "24px",
            paddingTop: "12px",
            paddingBottom: "12px",
            width: "100%",
          }}
        >
          {shift?.startDateTime &&
          shift?.endDateTime ? (
            <Box
              mt={1}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                paddingBottom: "8px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "16px",
                    lineHeight: "24px",
                    fontWeight: "500",
                    color: colors.title,
                  }}
                >
                  {getTranslatedDate({
                    dateStr: shift?.startDateTime,
                    formatString: "EEE, d MMM",
                    language: i18n.language,
                  })}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "16px",
                    lineHeight: "24px",
                    fontWeight: "500",
                    color: colors.title,
                  }}
                >
                  {format(
                    new Date(shift?.startDateTime),
                    "h:mm a"
                  )}{" "}
                  -{" "}
                  {format(
                    new Date(shift?.endDateTime),
                    "h:mm a"
                  )}{" "}
                  {"(" +
                    getHourMinuteDifference(
                      new Date(shift?.endDateTime),
                      new Date(shift?.startDateTime)
                    )?.differenceInText +
                    ")"}
                </Typography>
              </Box>
            </Box>
          ) : null}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              paddingTop: "4px",
              paddingBottom: "4px",
            }}
          >
            <DirectionsBusOutlined
              style={{
                fontSize: "20px",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                paddingRight: "8px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "20px",
                  color: "rgba(0,0,0,0.6)",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                }}
              >
                {shift?.shiftName ?? "-"}
              </Typography>
            </Box>
          </Box>
        </Box>
        {infoBarText && canShowEstimatedTime && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              backgroundColor: colors.infoBox,
              width: "100%",
              padding: "12px 16px",
              borderRadius: "0px 0px 8px 8px",
            }}
          >
            <InfoOutlined sx={{
              fontSize: "20px",
              color: colors.infoIcon
            }} />
            <Typography sx={{
              lineHeight: "20px",
              fontFamily: "Roboto",
              fontSize: "14px",
              fontWeight: "400",
              marginLeft: "8px",
              color: colors.infoText,
            }}>
              {infoBarText}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Container maxWidth="sm" disableGutters className={classes.pageContainer}>
      <Header />
      <Box
        ref={scrollViewRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {shiftCard()}
            <FormControl variant="filled">
              <InputLabel id="select-a-time-off-label">
                {i18n.t("apply-for-leave-time-off-type")}
              </InputLabel>
              <Select
                ref={dropdownRefForleaveRequestType}
                labelId={"select-a-time-off-label"}
                variant={"filled"}
                label={i18n.t("apply-for-leave-time-off-type")}
                value={selectedLeaveType}
                onChange={(e) => {
                  const selectedItem = e.target.value ?? {
                    id: null,
                    reasonText: "",
                  };
                  let isSame = false;
                  if (selectedLeaveType) {
                    if (selectedLeaveType.id === selectedItem.id) {
                      isSame = true;
                    }
                  }
                  if (selectedItem.id !== null && !isSame) {
                    setSelectedLeaveType(selectedItem);
                    setIsPairingAccordionOpen(false);
                    setSelectedLeaveType2(null);
                    setDuration2(null);
                    try {
                      if (type === SHIFT_CANCEL_CODE) {
                        // Cover full shift duration
                        const options = generateDurationOptionsByMinutes(
                          shiftDurationInMinutes,
                          selectedItem.incrementHours
                        );
                        const lastOption = options?.[options.length - 1]?.timeInMinutes;
                        if (lastOption !== shiftDurationInMinutes) {
                          const completeOptions = [
                            ...options,
                            {
                              text: getLocalizedDurationString(shiftDurationInMinutes),
                              timeInMinutes: shiftDurationInMinutes,
                            },
                          ];
                          setDurationOptions(completeOptions);
                          setDuration(
                            completeOptions?.[completeOptions.length - 1].timeInMinutes ?? null
                          );
                        } else {
                          setDurationOptions(options);
                          setDuration(
                            options?.[options.length - 1]?.timeInMinutes ?? null
                          );
                        }
                      } else {
                        setHours(defaultHours);
                        setSelectedHourIndex(0);
                        setMinutes(defaultMinutes);
                        setSelectedMinutesIndex(0);
                        setAmpm(defaultAmPm);
                        setSelectedAmpmIndex(0);
                        const options = generateDurationOptionsByMinutes(
                          shiftDurationInMinutes - 1,
                          selectedItem.incrementHours
                        );
                        setDurationOptions(options);
                        setDuration(options?.[0]?.timeInMinutes ?? null);
                      }
                    } catch (e) {
                      // Do nothing
                    }
                  }
                }}
                sx={{
                  backgroundColor: colors.fieldBackground,
                  "& .MuiSelect-select": {
                    backgroundColor: colors.fieldBackground,
                  },
                  "& .MuiMenuItem-root": {
                    backgroundColor: colors.fieldBackground,
                  },
                  "& .MuiMenuItem-root.Mui-selected": {
                    backgroundColor: `${colors.fieldBackground} !important`,
                  },
                  marginBottom: "12px",
                }}
              >
                {availableLeaveTypes?.map((reason: any) => (
                  <MenuItem key={reason.id} value={reason}>
                    {renderLeaveText(reason)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedLeaveType
              && hours.length > 0
                && durationOptions?.length > 0 &&
              (
                <FormControl variant="filled">
                  <InputLabel id="select-a-time-off-duration-label">
                    {i18n.t("apply-for-leave-duration-hours-text")}
                  </InputLabel>
                  <Select
                    ref={dropdownRefForDurationType}
                    labelId={"select-a-time-off-duration-label"}
                    variant={"filled"}
                    label={i18n.t("apply-for-leave-duration-hours-text")}
                    value={duration ?? null}
                    disabled={(type === SHIFT_CANCEL_CODE && !selectedLeaveType2) || durationOptions.length === 0}
                    onChange={(e) => {
                      const selectedItem = Number(e.target.value) ?? 0;
                      let isSame = false;
                      if (duration) {
                        if (duration === selectedItem) {
                          isSame = true;
                        }
                      }
                      if (!isSame && selectedItem !== 0) {
                        setDuration(selectedItem ?? null);
                        if (selectedLeaveType2) {
                          // If secondary type is selected, update its duration
                          if (type === SHIFT_CANCEL_CODE) {
                            const remainingMinutes = shiftDurationInMinutes - selectedItem;
                            const options = generateDurationOptionsByMinutes(
                              remainingMinutes,
                              selectedLeaveType2.incrementHours
                            );
                            const lastOption = options?.[options.length - 1]?.timeInMinutes;
                            if (lastOption !== remainingMinutes) {
                              const completeOptions = [...options, {
                                text: getLocalizedDurationString(remainingMinutes),
                                timeInMinutes: remainingMinutes,
                              }];
                              setDurationOptions2(completeOptions);
                              setDuration2(completeOptions?.[completeOptions.length - 1]?.timeInMinutes ?? null);
                            } else {
                              setDurationOptions2(options);
                              setDuration2(options?.[options.length - 1]?.timeInMinutes ?? null);
                            }
                          } else {
                            const remainingMinutes = shiftDurationInMinutes - selectedItem;
                            const maxSecondaryDuration = remainingMinutes - 1;
                            const options = generateDurationOptionsByMinutes(
                              maxSecondaryDuration >= 0 ? maxSecondaryDuration : 0,
                              selectedLeaveType2.incrementHours
                            );
                            setDurationOptions2(options);
                            setDuration2(options?.[0]?.timeInMinutes ?? null);
                          }
                        }
                      }
                    }}
                    sx={{
                      backgroundColor: colors.fieldBackground,
                      "& .MuiSelect-select": {
                        backgroundColor: colors.fieldBackground,
                      },
                      "& .MuiMenuItem-root": {
                        backgroundColor: colors.fieldBackground,
                      },
                      "& .MuiMenuItem-root.Mui-selected": {
                        backgroundColor: `${colors.fieldBackground} !important`,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: colors.fieldBackground,
                        "& .MuiSelect-select": {
                          color: colors.fieldText, // Keep text dark in disabled state
                          WebkitTextFillColor: colors.fieldText, // Ensures text color consistency for Safari
                        },
                        "& .MuiSelect-icon": {
                          visibility: "hidden", // Hide the dropdown arrow
                        },
                      },

                      marginBottom: "12px",
                    }}
                  >
                    {durationOptions?.map((item: any) => (
                      <MenuItem key={`first-leave-type-${item.timeInMinutes}`} value={item.timeInMinutes}>
                        <Typography
                          sx={{
                            color: colors.fieldText,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            {showPairingAccordion && (
              <Box sx={{
                backgroundColor: colors.greyLight,
                borderRadius: "4px",
                marginBottom: "12px",
                cursor: "pointer",
              }}>
                <Box sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingLeft: "12px",
                  paddingRight: "8px",
                  paddingTop: "16px",
                  paddingBottom: "16px",
                }}
                 onClick={() => {
                   setIsPairingAccordionOpen(!isPairingAccordionOpen);
                   setSelectedLeaveType2(null);
                   setDuration2(null);
                   setDurationOptions2([]);
                 }}
                >
                  <Typography sx={{
                    fontFamily: "Roboto",
                    fontSize: "14px",
                    fontWeight: 400,
                    color: colors.title,
                  }}>
                    {t("apply-for-leave_second-time-off-type_optional_pairing-enabled")}
                  </Typography>
                  {isPairingAccordionOpen ? (
                    <RemoveOutlined sx={{
                      color: colors.title,
                      fontSize: "20px",
                    }} />
                  ) : (
                    <AddOutlined sx={{
                      color: colors.title,
                      fontSize: "20px",
                    }} />
                  )}
                </Box>
                {isPairingAccordionOpen && (
                  <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    borderTop: `1px solid ${colors.border}`,
                    width: "100%",
                  }}>
                    <FormControl variant="filled">
                      <InputLabel id="select-a-2nd-time-off-label">
                        {i18n.t("apply-for-leave-time-off-type")}
                      </InputLabel>
                      <Select
                        ref={dropdownRefForleaveRequestType2}
                        labelId={"select-a-2nd-time-off-label"}
                        variant={"filled"}
                        label={i18n.t("apply-for-leave-time-off-type")}
                        value={selectedLeaveType2}
                        onChange={(e) => {
                          const selectedItem = e.target.value ?? {
                            id: null,
                            reasonText: "",
                          };
                          let isSame = false;
                          if (selectedLeaveType2) {
                            if (selectedLeaveType2.id === selectedItem.id) {
                              isSame = true;
                            }
                          }
                          if (!isSame && selectedItem.id !== null) {
                            setSelectedLeaveType2(selectedItem);
                            try {
                              if (type === SHIFT_CANCEL_CODE) {
                                if (duration) {
                                  // Cover full shift duration
                                  const options = generateDurationOptionsByMinutes(
                                    shiftDurationInMinutes - duration,
                                    selectedLeaveType2.incrementHours
                                  );
                                  const lastOption = options?.[options.length - 1]?.timeInMinutes;
                                  if (lastOption !== shiftDurationInMinutes - duration) {
                                    const completeOptions = [
                                      ...options,
                                      {
                                        text: getLocalizedDurationString(shiftDurationInMinutes - duration),
                                        timeInMinutes: shiftDurationInMinutes - duration,
                                      },
                                    ];
                                    setDurationOptions2(completeOptions);
                                    setDuration2(
                                      completeOptions?.[completeOptions.length - 1].timeInMinutes ?? null
                                    );
                                  } else {
                                    setDurationOptions2(options);
                                    setDuration2(options?.[options.length - 1]?.timeInMinutes ?? null);
                                  }
                                }
                              } else {
                                setHours(defaultHours);
                                setSelectedHourIndex(0);
                                setMinutes(defaultMinutes);
                                setSelectedMinutesIndex(0);
                                setAmpm(defaultAmPm);
                                setSelectedAmpmIndex(0);
                                const options = generateDurationOptionsByMinutes(
                                  shiftDurationInMinutes - (duration ?? 0) - 1,
                                  selectedLeaveType2.incrementHours
                                );
                                setDurationOptions2(options);
                                setDuration2(options?.[options.length - 1]?.timeInMinutes ?? null);
                              }
                            } catch (e) {
                              // Do nothing
                            }
                          }
                        }}
                        sx={{
                          backgroundColor: colors.fieldBackground,
                          "& .MuiSelect-select": {
                            backgroundColor: colors.fieldBackground,
                          },
                          "& .MuiMenuItem-root": {
                            backgroundColor: colors.fieldBackground,
                          },
                          "& .MuiMenuItem-root.Mui-selected": {
                            backgroundColor: `${colors.fieldBackground} !important`,
                          },
                          marginBottom: "12px",
                        }}
                      >
                        {availableLeaveTypes?.filter(
                          (reason: any) =>
                            reason.pairingEnabled && reason.id !== selectedLeaveType?.id
                        )?.map((reason: any) =>
                          renderSecondLeaveText(reason, duration ?? 0, shiftDurationInMinutes, type)
                        )}
                      </Select>
                    </FormControl>
                    {selectedLeaveType2 &&
                      durationOptions2?.length > 0 &&
                      hours.length > 0 && (
                        <FormControl variant="filled">
                          <InputLabel id="select-a-2nd-duration-label">
                            {i18n.t("apply-for-leave-duration-hours-text")}
                          </InputLabel>
                          <Select
                            ref={dropdownRefForDurationType2}
                            labelId={"select-a-2nd-duration-label"}
                            variant={"filled"}
                            label={i18n.t("apply-for-leave-duration-hours-text")}
                            value={duration2 ?? null}
                            disabled={type === SHIFT_CANCEL_CODE || durationOptions2.length === 0}
                            onChange={(e) => {
                              const selectedItem = Number(e.target.value) ?? 0;
                              let isSame = false;
                              if (duration2) {
                                if (duration2 === selectedItem) {
                                  isSame = true;
                                }
                              }
                              if (!isSame && selectedItem !== 0) {
                                setDuration2(selectedItem ?? null);
                              }
                            }}
                            sx={{
                              backgroundColor: colors.fieldBackground,
                              "& .MuiSelect-select": {
                                backgroundColor: colors.fieldBackground,
                              },
                              "& .MuiMenuItem-root": {
                                backgroundColor: colors.fieldBackground,
                              },
                              "& .MuiMenuItem-root.Mui-selected": {
                                backgroundColor: `${colors.fieldBackground} !important`,
                              },
                              "&.Mui-disabled": {
                                backgroundColor: colors.fieldBackground,
                                "& .MuiSelect-select": {
                                  color: colors.fieldText, // Keep text dark in disabled state
                                  WebkitTextFillColor: colors.fieldText, // Ensures text color consistency for Safari
                                },
                                "& .MuiSelect-icon": {
                                  visibility: "hidden", // Hide the dropdown arrow
                                },
                              },
                              marginBottom: "12px",
                            }}
                          >
                            {durationOptions2?.map((item: any) => (
                              <MenuItem key={`second-leave-type-${item.timeInMinutes}`} value={item.timeInMinutes}>
                                <Typography
                                  sx={{
                                    color: colors.fieldText,
                                  }}
                                >
                                  {item.text}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                  </Box>
                )}
              </Box>
            )}
            <MessageBox
              text={workerComment}
              placeholder={t("apply-for-leave-absence-reason-placeholder")}
              setText={setWorkerComment}
              onBlur={() => setEditingMessage(false)}
              onFocus={() => setEditingMessage(true)}
              editing={editingMessage}
              showFooterText={false}
              maxChars={50}
            />
      </Box>
      {/* Sticky Footer */}

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          flexDirection: "row",
          backgroundColor: "#fff",
          padding: "16px 8px",
          boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            width: "30%",
            paddingRight: "8px",
            paddingLeft: "8px",
          }}
        >
          <Button
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              alignSelf: "stretch",
              borderRadius: "4px",
              padding: "10px 24px",
              height: "44px",
              width: "100%",
            }}
            onClick={() => {
              try {
                dispatch(closeLeaveRequestPage());
                dispatch(resetSubmitLeaveRequest());
              } catch (e) {
                // Handle error
              }
            }}
            variant="outlined"
          >
            {t("apply-for-leave_cancel")}
          </Button>
        </Box>
        <Box
          sx={{
            width: "70%",
            paddingLeft: "8px",
            paddingRight: "8px",
          }}
        >
          <Button
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              alignSelf: "stretch",
              borderRadius: "4px",
              padding: "10px 24px",
              height: "44px",
              width: "100%",
            }}
            onClick={
            () => {
              const submitLeave = () => {
                // Based on API Signature for Leave Request API /workers/request
                const leaveApplication: any = {
                  requestTypeId: requestTypeId ? requestTypeId : null,
                  companyId: companyId, // Required
                  startDate: timeOffEnabled
                    ? getStartDate()
                    : shift.startDateTime, // Required
                  endDate: timeOffEnabled
                    ? getEndDate()
                    : shift.endDateTime, // Required
                  workerComment: workerComment, // Optional
                  ftShiftId: shift.ftShiftId,
                  leaveTypes: [],
                };
                if (selectedLeaveType && duration) {
                  leaveApplication.leaveTypes.push({
                      leaveTypeId: selectedLeaveType.id,
                      requestedHours: duration/60,
                  })
                }
                if (selectedLeaveType2 && duration2) {
                  leaveApplication.leaveTypes.push({
                    leaveTypeId: selectedLeaveType2.id,
                    requestedHours: duration2/60,
                  });
                }
                dispatch(
                  presetSubmitLeaveRequest({
                    ftShiftId: shift.ftShiftId,
                    startDateTime: leaveApplication.startDate,
                    endDateTime: leaveApplication.endDate,
                  })
                );
                dispatch(submitLeaveRequest(leaveApplication));
              }
              if (submitLeaveRequestStatus === "idle") {
                const selectedLeaveTypeBalanceInMinutes = selectedLeaveType?.isNoLimit
                  ? Infinity
                  : (selectedLeaveType?.availableHrs ?? 0) * 60;
                const selectedLeaveType2BalanceInMinutes = selectedLeaveType2?.isNoLimit
                  ? Infinity
                  : (selectedLeaveType2?.availableHrs ?? 0) * 60;
                if ((duration && duration > selectedLeaveTypeBalanceInMinutes)
                  || (duration2 && duration2 > selectedLeaveType2BalanceInMinutes)
                ) {
                  confirm({
                    confirmationButtonProps: {
                      variant: "contained",
                      color: "primary",
                    },
                    dialogProps: {
                      slotProps: {
                        paper: {
                          sx: {
                            p: "21px 21px 17px 21px",
                          },
                        }
                      }
                    },
                    title: <Typography
                      component="div"
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "20px",
                        fontWeight: 500,
                        lineHeight: "32px",
                        color: "rgba(0,0,0,0.87)",
                      }}>{i18next.t("view-regular-shift-dialog_cancel-request-title")}</Typography>,
                    description: <Typography sx={{
                      fontFamily: "Roboto",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "20px",
                      color: "rgba(0,0,0,0.87)",
                      marginTop: "16px",
                    }}>
                      {i18next.t("apply-for-leave_insufficient-balance")}
                    </Typography>,
                    confirmationText:  i18next.t("apply-for-leave_submit-request"),
                    cancellationText: i18next.t("apply-for-leave_cancel"),
                  })
                    .then(() => {
                      /* handle success */
                      submitLeave();
                    })
                    .catch(() => {
                      /* handle error */
                    });
                } else {
                  submitLeave();
                }
              }
            }}
            disabled={!canSubmit}
            variant="contained"
          >
            {submittingLeaveRequest
              ? t("apply-for-leave_submitting")
              : t("apply-for-leave_submit-request")}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={
          submitLeaveRequestStatus === "error" &&
          submitLeaveRequestErrorMessage !== ""
        }
        autoHideDuration={3000}
        message={t("apply-for-leave_time-off-request-failed")}
        onClose={() => {
          dispatch(resetSubmitLeaveRequest());
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          maxWidth="sm"
          sx={{
            backgroundColor: "black",
            color: "#fff",
            py: 2,
            px: 1,
            borderRadius: 1,
          }}
        >
          <Box display="flex" flexDirection="row" alignItems="center" flex={1}>
            <Box marginLeft={2} marginRight={4}>
              <Typography sx={{ fontSize: "16px", color: "#fff" }}>
                {submitLeaveRequestErrorMessage}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => dispatch(resetSubmitLeaveRequest())}>
            <CloseIcon sx={{ color: "#fff" }} />
          </IconButton>
        </Box>
      </Snackbar>
    </Container>
  );
};

export default LeaveRequestPage;
